import type { ComponentType } from 'react'
import { useEffect, useMemo, useState, type FC } from 'react'

import type { AssetType } from '@skytech/types'
import {
  AssetFallbackIcon,
  BasicTable,
  INITIAL_PER_PAGE_LIMIT,
  Input,
  LoaderWrapper,
  NotificationBubble,
  StyledText,
  Tooltip,
} from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { Container } from '@skytech/ui/Container'
import { ImageWithIconFallback } from '@skytech/ui/Image'
import { ASSET_TYPES, getWhiteLabelForDealerStyle } from '@skytech/utils'
import { useDebounce } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'
import type { CellContext, ColumnDef, PaginationState, SortingState } from '@tanstack/react-table'
import CloseIcon from 'mdi-react/CloseIcon'

import type { Product, ProductSort } from '@/api/products'
import { productsResources } from '@/api/products'
import queries from '@/api/queries'
import { useAppContext } from '@/App.context'
import { useTranslation } from '@/components/i18n/hooks'
import { Translation } from '@/components/i18n/Translation'
import Highlight from '@/components/util/Highlight'
import { useSearchParamsByKey } from '@/hooks/searchParams'

export const MainProducts: FC<{
  selectedCategory: string
  selectedProductId?: number
  numberOfRelated?: number
  onProductSelect: (id?: number) => void
}> = ({ selectedCategory, selectedProductId, numberOfRelated, onProductSelect }) => {
  const translate = useTranslation()
  const { whiteLabel } = useAppContext()

  const [querySearch, setQuerySearch] = useSearchParamsByKey('search')
  const [searchFilter, setSearchFilter] = useState(querySearch || '')
  const debouncedSearchTerm = useDebounce(searchFilter, 500)

  const [sorting, setSorting] = useState<SortingState>([])

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: INITIAL_PER_PAGE_LIMIT,
  })

  useEffect(() => {
    setSearchFilter(querySearch || '')
  }, [querySearch])

  useEffect(() => {
    setQuerySearch(debouncedSearchTerm)
  }, [debouncedSearchTerm, setQuerySearch])

  const { data, isLoading } = useQuery(
    queries.products({
      sorting: {
        sortBy: sorting.length === 0 ? undefined : (String(sorting[0]?.id) as keyof Product),
        sort: sorting.length === 0 ? undefined : sorting[0]?.desc ? 'desc' : 'asc',
      },
      columnFilters: {
        keyword: querySearch || undefined,
        category: selectedCategory !== 'ALL' ? selectedCategory : undefined,
      },
      pagination: {
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
      },
    }),
    () => {
      let productSorting: ProductSort = { sortBy: undefined, sort: undefined }
      if (sorting.length !== 0) {
        productSorting = {
          sortBy: String(sorting[0]?.id) as keyof Product,
          sort: sorting[0]?.desc ? 'desc' : 'asc',
        }
      }
      return productsResources.getProducts({
        sorting: productSorting,
        columnFilters: {
          keyword: querySearch || undefined,
          category: selectedCategory !== 'ALL' ? selectedCategory : undefined,
        },
        pagination: {
          page: pagination.pageIndex + 1,
          limit: pagination.pageSize,
        },
      })
    }
  )

  const productColumns = useMemo<ColumnDef<Product>[]>(
    () => [
      {
        header: 'Product name',
        accessorKey: 'productName',
        cell: info => NameCell(info, searchFilter, { selectedProductId, numberOfRelated }),
      },
    ],
    [searchFilter, selectedProductId, numberOfRelated]
  )

  return (
    <Container className="mx-0 h-[55rem] rounded-lg border-2 border-solid border-black border-zinc-200 bg-white px-0 shadow-lg">
      <Input
        id="search"
        type="text"
        wrapperClassName="items-start mx-2 mb-2"
        value={searchFilter}
        placeholder={translate('Search')}
        onChange={e => setSearchFilter(e.target.value)}
        RightElement={
          <Button
            variant="tertiary"
            className="m-0 bg-transparent p-0"
            isIconRight
            Icon={<CloseIcon />}
            onClick={() => setSearchFilter('')}
          />
        }
      />
      <LoaderWrapper isLoading={isLoading} whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}>
        <BasicTable
          isSingleSelection
          dataWithPagination={data}
          columns={productColumns}
          pagination={pagination}
          setPagination={setPagination}
          sorting={sorting}
          setSorting={setSorting}
          onTrClick={onProductSelect}
          selectedRows={selectedProductId ? [selectedProductId] : []}
          wrapperClassName="h-[50rem] overflow-y-auto"
          TranslationComponent={Translation as ComponentType}
        />
      </LoaderWrapper>
    </Container>
  )
}

function NameCell(
  info: CellContext<Product, unknown>,
  textSearch: string,
  badgeData: { selectedProductId?: number; numberOfRelated?: number }
) {
  const translate = useTranslation()
  const { whiteLabel } = useAppContext()

  const row = info.row.original
  const assetType =
    row.category && ASSET_TYPES.includes(row.category as AssetType)
      ? (row.category as AssetType)
      : 'OTHER'

  return (
    <div className="flex w-full flex-row gap-2">
      <div className="flex w-full flex-row gap-2">
        <ImageWithIconFallback
          alt="asset"
          src={row.imageUrl || ''}
          className="shadow-basic flex h-[3.7rem] w-[3.7rem] min-w-[3.7rem] max-w-full items-center justify-center rounded-full bg-white p-1 shadow transition duration-200 ease-in-out max-[500px]:h-12 max-[500px]:w-12 sm:h-[3.7rem] sm:w-[3.7rem]"
          fallbackIcon={<AssetFallbackIcon assetType={assetType} />}
        />
        <div className="flex flex-col justify-start gap-0.5 text-start">
          <StyledText
            colorMode="black"
            size="regular-plus"
            fontWeight="standard"
            className="font-semibold"
          >
            <Highlight pattern={textSearch} text={row.productName || row.articleName} />
          </StyledText>

          {row.manufacturerProductNo && (
            <StyledText colorMode="gray" size="small" fontWeight="standard">
              <Translation id="Manufacture nmb.:" />{' '}
              <span className="font-semibold">
                <Highlight pattern={textSearch} text={row.manufacturerProductNo} />
              </span>
            </StyledText>
          )}

          {row.articleNo === row.vendorUniqueId && (
            <StyledText colorMode="gray" size="small" fontWeight="standard">
              <Translation id="Article nmb.:" />{' '}
              <span className="font-semibold">
                <Highlight pattern={textSearch} text={row.articleNo} />
              </span>
            </StyledText>
          )}

          {row.articleNo !== row.vendorUniqueId && (
            <>
              {row.articleNo && (
                <StyledText colorMode="gray" size="small" fontWeight="standard">
                  <Translation id="Article nmb.:" />{' '}
                  <span className="font-semibold">
                    <Highlight pattern={textSearch} text={row.articleNo} />
                  </span>
                </StyledText>
              )}
              {row.vendorUniqueId && (
                <StyledText colorMode="gray" size="small" fontWeight="standard">
                  <Translation id="Vendor unique ID:" />{' '}
                  <span className="font-semibold">
                    {' '}
                    <Highlight pattern={textSearch} text={row.vendorUniqueId} />
                  </span>
                </StyledText>
              )}
            </>
          )}

          {row.category && (
            <StyledText colorMode="gray" size="small" fontWeight="standard">
              <Translation id={row.category} />
            </StyledText>
          )}
        </div>
      </div>

      {Boolean(badgeData.selectedProductId === row.id && badgeData.numberOfRelated) && (
        <div className="mr-4 justify-self-end">
          <Tooltip id="selected-products-tooltip" style={{ zIndex: 9999 }} />
          <span
            data-tooltip-id="selected-products-tooltip"
            data-tooltip-content={`${translate('Selected:')} ${badgeData.numberOfRelated}`}
          >
            <NotificationBubble
              isDealer
              amount={badgeData.numberOfRelated}
              whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
            />
          </span>
        </div>
      )}
    </div>
  )
}
